import { authHeaders } from '@/lib/authHeaders';

/** Web Push client: register the service worker, subscribe this device with
 *  the server's VAPID key, and keep the subscription registered per account. */

export function pushSupported(): boolean {
  return 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
}

export function pushPermission(): NotificationPermission | 'unsupported' {
  return pushSupported() ? Notification.permission : 'unsupported';
}

function urlB64ToUint8Array(base64: string): Uint8Array {
  const padding = '='.repeat((4 - (base64.length % 4)) % 4);
  const b64 = (base64 + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(b64);
  return Uint8Array.from([...raw].map((c) => c.charCodeAt(0)));
}

async function registration(): Promise<ServiceWorkerRegistration> {
  const reg = await navigator.serviceWorker.register('/sw.js');
  await navigator.serviceWorker.ready;
  return reg;
}

/** Ask permission (if needed), subscribe this device and register it. */
export async function enablePush(): Promise<'ok' | 'denied' | 'error'> {
  try {
    if (!pushSupported()) return 'error';
    const perm = await Notification.requestPermission();
    if (perm !== 'granted') return 'denied';
    const reg = await registration();
    const keyRes = await fetch('/api/account/push-key', { headers: await authHeaders() });
    if (!keyRes.ok) return 'error';
    const { key } = (await keyRes.json()) as { key: string };
    const sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlB64ToUint8Array(key) as BufferSource,
    });
    const r = await fetch('/api/account/push-subscribe', {
      method: 'POST',
      headers: { 'content-type': 'application/json', ...(await authHeaders()) },
      body: JSON.stringify({ subscription: sub.toJSON() }),
    });
    return r.ok ? 'ok' : 'error';
  } catch { return 'error'; }
}

/** Unsubscribe this device. */
export async function disablePush(): Promise<void> {
  try {
    if (!pushSupported()) return;
    const reg = await navigator.serviceWorker.getRegistration('/sw.js');
    const sub = await reg?.pushManager.getSubscription();
    if (sub) {
      await fetch('/api/account/push-unsubscribe', {
        method: 'POST',
        headers: { 'content-type': 'application/json', ...(await authHeaders()) },
        body: JSON.stringify({ endpoint: sub.endpoint }),
      }).catch(() => undefined);
      await sub.unsubscribe();
    }
  } catch { /* ignore */ }
}

/** Is THIS device currently subscribed? */
export async function pushEnabled(): Promise<boolean> {
  try {
    if (!pushSupported() || Notification.permission !== 'granted') return false;
    const reg = await navigator.serviceWorker.getRegistration('/sw.js');
    return !!(await reg?.pushManager.getSubscription());
  } catch { return false; }
}

/** App start: if permission was already granted, silently refresh the
 *  subscription registration (endpoints rotate; accounts change). */
export async function refreshPushIfGranted(): Promise<void> {
  try {
    if (!pushSupported() || Notification.permission !== 'granted') return;
    const reg = await registration();
    const sub = await reg.pushManager.getSubscription();
    if (sub) {
      await fetch('/api/account/push-subscribe', {
        method: 'POST',
        headers: { 'content-type': 'application/json', ...(await authHeaders()) },
        body: JSON.stringify({ subscription: sub.toJSON() }),
      }).catch(() => undefined);
    }
  } catch { /* ignore */ }
}
